// src/context/brandVoice/ui/FieldRow.jsx — ONE field of the brand-guidelines document.
//
// The frame every document tab shares: the label, the owner chip, Freeze / Unlock, the
// English badge with "Traduci ora", the completeness hint, the conflict line, the
// problems — and, in the middle, the editor slot.
// ⛔⛔ It DECIDES NOTHING: every rule (which chip, whether Freeze or Unlock is offered,
// whether they are disabled, which English badge, whether "Traduci ora" is possible,
// which hint) comes from `fieldRules.js`, so a rule can never be re-derived here and
// drift from the module the tests pin. This file only lays them out.
// ⛔ The `field_path` is never shown — it is a backend coordinate, not copy. The only
// exception is the fallback label of a field key we do not know yet (the caller passes
// the raw path as `label`), so a section the backend adds later still renders.
// ⛔ No server sentence anywhere: a problem is a KEY into brandVoice.validation.*.
// v200: the English badge and "Traduci ora" show in every view (v199's `ctx.english` gate is gone).
import React from 'react';
import { useTranslation } from 'react-i18next';
import OwnerChip from './OwnerChip.jsx';
import {
  actionsDisabled, canFreeze, canTranslateNow, canUnlock, completenessHint, englishBadge,
} from '../fieldRules.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../../constants/theme.js';

const ROW = {
  padding: '16px 0',
  borderTop: `1px solid ${COLORS.border}`,
  fontFamily: FONT_SANS,
};
// ⭐ v186 (decisions 4, 12): the three "avoid" lists are guardrails, not content — a 3px left border in the warning
// colour, keyed on the payload's `guardrail_level`. The marking exists because these lists LOOK like duplicates of
// each other and get deleted as such. v200: the "Guardrail · <level> · letto da: …" line is gone (the border stays).
// ⛔ Ownership is still not decided here: the `neverAI` chip and the missing Freeze / Unlock come from `fieldRules`.
const GUARDRAIL_ROW = { borderLeft: `3px solid ${SEMANTIC.attenzione.fg}`, paddingLeft: 12 };

const HEADER = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  flexWrap: 'wrap', gap: 8, marginBottom: 10,
};
const LABEL = { fontSize: 13, fontWeight: 600, color: COLORS.ink };
const MUTED = { fontSize: 11, color: COLORS.textTertiary };
const ACTION_BTN = (disabled) => ({
  padding: '5px 10px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
  color: disabled ? COLORS.textTertiary : COLORS.ink,
  fontFamily: FONT_SANS, fontSize: 12, fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
});
const NOTE = (sem) => ({
  marginTop: 8, padding: '6px 10px', borderRadius: RADII.controlInner,
  background: sem.bg, color: sem.fg, fontSize: 12, lineHeight: '18px',
});
const LINK_BTN = {
  marginLeft: 8, padding: 0, border: 'none', background: 'transparent',
  color: SEMANTIC.attenzione.fg, fontFamily: FONT_SANS, fontSize: 12,
  fontWeight: 700, textDecoration: 'underline', cursor: 'pointer',
};

export default function FieldRow({
  field, ctx, label, children, problems, conflict, onFreeze, onUnlock, onTranslate,
}) {
  const { t } = useTranslation('context');
  const disabled = actionsDisabled(ctx);
  const hint = completenessHint(field);
  // v200: a short description next to the name, for the fields whose name alone says little (USP, UVP).
  const description = t(`brandVoice.fieldDescription.${field.field_path}`, { defaultValue: '' });
  const badge = englishBadge(field, ctx);
  const translatable = canTranslateNow(field, ctx);
  const list = problems || [];
  // ⭐ v187: a record row's problem names its FIELD in our words (brandVoice.recordField,
  // raw key as the fallback) — never the backend key («term») — and a problem found on the
  // English half of a bilingual field says so. ⭐ v188: a claims row problem also names its
  // CATEGORY ("Metriche — …"): row numbers restart in every category — only when the
  // problem has BOTH a category and a row (`unknownCategory` already names its own).
  // ⛔ `validation.enSuffix` is a DEDICATED key, not the editors' `list.enSuffix`, so a wording
  // change on the lists cannot silently reword these messages.
  const problemText = (p) => {
    const params = p.params || {};
    const name = params.name
      ? t(`brandVoice.recordField.${params.name}.name`, { defaultValue: params.name })
      : params.name;
    const text = t([`brandVoice.validation.${p.key}`, `brandVoice.${p.key}`], { ...params, name });
    const prefix = params.category && params.row
      ? `${t(`brandVoice.claimCategory.${params.category}`, { defaultValue: params.category })} — `
      : '';
    const suffix = params.lang === 'en' ? t('brandVoice.validation.enSuffix') : '';
    return `${prefix}${text}${suffix}`;
  };
  return (
    // A group NAMED BY THE LABEL on every row: two str_list fields can share a tab with
    // identical generic item labels ("Voce 1"), so the group gives them their field's context.
    <div
      role="group"
      aria-label={label || field.field_path}
      style={{ ...ROW, ...(field.guardrail_level ? GUARDRAIL_ROW : null) }}
    >
      <div style={HEADER}>
        {/* v200: the claims field is the whole "Claim verificati" tab, which already names it — no visible label and no
            completeness hint there (the group keeps its accessible name). */}
        {field.widget === 'claims' ? <span /> : (
          <span style={{ display: 'inline-flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
            <span style={LABEL}>{label || field.field_path}</span>
            {description && <span style={MUTED}>{description}</span>}
            {field.required && <span style={{ ...MUTED, color: SEMANTIC.errore.fg }}>{t('brandVoice.required')}</span>}
            {hint && <span style={MUTED}>{t(`brandVoice.hint.${hint}`)}</span>}
          </span>
        )}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <OwnerChip field={field} ctx={ctx} />
          {canFreeze(field, ctx) && (
            <button type="button" disabled={disabled} onClick={() => onFreeze?.(field)} style={ACTION_BTN(disabled)}>
              {t('brandVoice.actions.freeze')}
            </button>
          )}
          {canUnlock(field, ctx) && (
            <button type="button" disabled={disabled} onClick={() => onUnlock?.(field)} style={ACTION_BTN(disabled)}>
              {t('brandVoice.actions.unlock')}
            </button>
          )}
        </span>
      </div>


      {/* The editor slot. Until the field's widget is registered the row still shows
          everything ELSE about the field, so its state is legible before it is editable. */}
      {children || <p style={{ fontSize: 13, color: COLORS.textTertiary, margin: 0 }}>{t('brandVoice.editorPending')}</p>}

      {/* ⛔ "Crediti esauriti" is a SENTENCE, never a link: a workspace admin need not be
          the company admin who alone can buy credits (plan decision 9). */}
      {(badge || translatable) && (
        <div style={NOTE(SEMANTIC.attenzione)}>
          {badge && <span>{t(`brandVoice.english.${badge}`)}</span>}
          {translatable && (
            <button type="button" onClick={() => onTranslate?.(field)} style={LINK_BTN}>
              {t('brandVoice.actions.translateNow')}
            </button>
          )}
        </div>
      )}

      {conflict && <div style={NOTE(SEMANTIC.attenzione)}>{t('brandVoice.conflict')}</div>}

      {list.length > 0 && (
        <div role="alert" style={NOTE(SEMANTIC.errore)}>
          {/* A client problem is worded from brandVoice.validation.*; the ONE problem the
              server raises (`invalidServer`, keyed from a 422's details.errors[].field_path)
              lives one level up, so the lookup falls back to brandVoice.* — ⛔ and either
              way it is OUR sentence, never the 422's English `message`. */}
          {list.map((p, i) => (
            <div key={`${p.key}-${i}`}>
              {problemText(p)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
